import { useState } from "react"
import { Button } from "@holigral/shared/ui/components/ui/button"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@holigral/shared/ui/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@holigral/shared/ui/components/ui/table"
import { useGetPurchasesQuery, useLazyDownloadInvoiceQuery } from "./api"
import formatMoney from "./formatMoney"
import type { Purchase } from "./model"
import { useT } from "@/i18n"
import { formatDate } from "@/utils"
import type { Uuid } from "@/utils"

export default function PurchaseHistory() {
  const { t } = useT("money")
  const { data } = useGetPurchasesQuery()
  const [downloadInvoice] = useLazyDownloadInvoiceQuery()
  const [downloadingId, setDownloadingId] = useState<Uuid | null>(null)

  const handleDownload = async (purchase: Purchase) => {
    if (!purchase.billingoInvoiceNumber) {
      return
    }
    setDownloadingId(purchase.id)
    try {
      const blob = await downloadInvoice(purchase.id).unwrap()
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      // Same filename pattern as the backend's Content-Disposition; a blob
      // object URL ignores that header, so the frontend must name it too.
      link.download = `profiling_invoice_${purchase.billingoInvoiceNumber}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } finally {
      setDownloadingId(null)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("history.title")}</CardTitle>
      </CardHeader>
      <CardContent>
        {data && data.items.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("history.date")}</TableHead>
                <TableHead>{t("history.creditsColumn")}</TableHead>
                <TableHead>{t("history.amount")}</TableHead>
                <TableHead>{t("history.invoice")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((purchase) => (
                <TableRow key={purchase.id}>
                  <TableCell>
                    {formatDate("date", purchase.createdAt)}
                  </TableCell>
                  <TableCell>{purchase.credits}</TableCell>
                  <TableCell>
                    {formatMoney({
                      amount: purchase.amount,
                      currency: purchase.currency,
                    })}
                  </TableCell>
                  <TableCell>
                    {purchase.billingoInvoiceNumber ? (
                      <Button
                        variant="ghost"
                        className="h-auto p-0"
                        disabled={downloadingId === purchase.id}
                        onClick={() => {
                          void handleDownload(purchase)
                        }}
                      >
                        {purchase.billingoInvoiceNumber}
                      </Button>
                    ) : (
                      <span className="text-muted-foreground">
                        {t("history.invoicePending")}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="text-sm text-muted-foreground">{t("history.empty")}</p>
        )}
      </CardContent>
    </Card>
  )
}
